import { api } from "@/shared/api";

const BASE = "/tenant/map-leads";

/** 规则分逐项明细(权重唯一出处在后端 MapLeadScoreService,前端只展示不复算)。 */
export interface ScoreEntry {
  label: string;
  points: number;
  max: number;
}

/** 一条候选企业(搜索响应内活着,不落库;加入线索时快照)。 */
export interface MapLeadItem {
  poiId: string;
  name: string;
  industry: string | null;
  address: string | null;
  phone: string | null;
  longitude: number | null;
  latitude: number | null;
  distanceMeters: number | null;
  districtName: string | null;
  businessArea: string | null;
  openTime: string | null;
  rating: number | null;
  score: number;
  level: string;
  scoreDetail: ScoreEntry[];
  reasons: string[];
}

/** 每日搜索配额(守平台侧高德账单;0901 拍板起搜索另扣 2 积分/次,价签走服务端价目表)。 */
export interface MapLeadQuota {
  limit: number;
  used: number;
  remaining: number;
}

export interface MapLeadSearchResponse {
  /** 本页条数。★高德 v5 无命中总数(count=本页条数,2026-08-23 真 key 实测),翻页靠 hasMore。 */
  pageCount: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
  items: MapLeadItem[];
  quota: MapLeadQuota;
}

export interface MapLeadSearchRequest {
  city?: string;
  district?: string;
  keyword?: string;
  industry?: string;
  searchMode: "KEYWORD" | "AROUND";
  longitude?: number;
  latitude?: number;
  radius?: number;
  page?: number;
  pageSize?: number;
  /** 主推产品(选填,2026-09-02):在售 product 叶子 nodeCode;后端非法值静默忽略。 */
  productNodeCode?: string;
  /**
   * 主推产品自由输入(选填,2026-09-07):没建产品库、或想手填产品名的租户走这个。
   * 语义 = nodeCode 优先,productText 兜底:productNodeCode 缺席/非法时后端才会用它
   * 并入评分与军师分析;两者都给时以 productNodeCode 为准。
   */
  productText?: string;
}

export interface ConvertRequest {
  poiId: string;
  name: string;
  phone?: string | null;
  address?: string | null;
  industry?: string | null;
  longitude?: number | null;
  latitude?: number | null;
  ruleScore?: number;
  scoreDetail?: ScoreEntry[];
}

/** duplicated=true:该 POI 此前已在线索池(幂等,不是错误)。 */
export interface ConvertResult {
  leadId: number;
  duplicated: boolean;
}

export interface MapLeadRow {
  id: number;
  name: string;
  phone: string | null;
  address: string | null;
  industry: string | null;
  ruleScore: number | null;
  source: string;
  sourceRefId: string;
  /** 军师分析结果 JSON 原文(null=未分析);用 parseAnalysis 解析后展示。 */
  aiAnalysis: string | null;
  createdAt: string;
}

/** 解析线索上持久化的军师分析 JSON;坏数据返 null(回看缺席优于报错)。 */
export function parseAnalysis(raw: string | null | undefined): MapLeadAnalysis | null {
  if (!raw) return null;
  try {
    const d = JSON.parse(raw) as Partial<MapLeadAnalysis>;
    if (!d || typeof d.summary !== "string" || !d.summary) return null;
    return {
      summary: d.summary,
      recommendedProduct: d.recommendedProduct ?? "",
      entryPoint: d.entryPoint ?? "",
      opening: d.opening ?? "",
      nextAction: d.nextAction ?? "",
      reasons: Array.isArray(d.reasons) ? d.reasons.filter((r) => typeof r === "string") : [],
    };
  } catch {
    return null;
  }
}

/** 军师商机分析结果(ai 收敛后的固定形状;按次计费,失败不扣)。 */
export interface MapLeadAnalysis {
  summary: string;
  recommendedProduct: string;
  entryPoint: string;
  opening: string;
  nextAction: string;
  reasons: string[];
}

export interface LeadPage {
  total: number;
  page: number;
  pageSize: number;
  items: MapLeadRow[];
}

export const mapLeadService = {
  search: (req: MapLeadSearchRequest) => api.post<MapLeadSearchResponse>(`${BASE}/search`, req),
  quota: () => api.get<MapLeadQuota>(`${BASE}/quota`),
  convert: (req: ConvertRequest) => api.post<ConvertResult>(`${BASE}/convert`, req),
  batchConvert: (items: ConvertRequest[]) =>
    api.post<ConvertResult[]>(`${BASE}/batch-convert`, { items }),
  leads: (page = 1, pageSize = 20) =>
    api.get<LeadPage>(`${BASE}/leads?page=${page}&pageSize=${pageSize}`),
  deleteLead: (id: number) => api.delete<void>(`${BASE}/leads/${id}`),
  /**
   * 让军师分析(按次计费)。主推产品选填,2026-09-07 起 nodeCode 优先、productText 兜底
   * (语义与 search 一致):都缺席就让军师自行判断,不并入产品定向。
   */
  analyzeLead: (id: number, productNodeCode?: string, productText?: string) =>
    api.post<MapLeadAnalysis>(`${BASE}/leads/${id}/analyze`, {
      ...(productNodeCode ? { productNodeCode } : {}),
      ...(productText ? { productText } : {}),
    }),
};

/** 把候选企业整形成加入线索的请求体(快照规则分)。 */
export function toConvertRequest(item: MapLeadItem): ConvertRequest {
  return {
    poiId: item.poiId,
    name: item.name,
    phone: item.phone,
    address: item.address,
    industry: item.industry,
    longitude: item.longitude,
    latitude: item.latitude,
    ruleScore: item.score,
    scoreDetail: item.scoreDetail,
  };
}
